"use client";

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { TERMS_TOP_ROW_CARD_HEIGHT } from "@/components/home/terms-card-spacing";
import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";
import { cn } from "@/lib/utils";

import {
  FINANCING_END,
  FINANCING_START,
  FEE_END,
  FEE_START,
  MOCK_ENTRANCE_END,
  PRICING_DURATION_MS,
  PricingBrowserMock,
  pricingPlaybackEase,
  segmentProgress,
  TOTAL_END,
  TOTAL_START,
} from "./pricing-card";
import type { TermsCopy } from "./types";
import { useTermsCardMotion } from "./use-terms-card-motion";
import type {
  TermsMotionDebugProps,
  TermsMotionState,
} from "./use-terms-card-motion";

export type OdometerToken =
  | { type: "digit"; value: number }
  | { type: "literal"; value: string };

export function tokenizeOdometerValue(value: string): OdometerToken[] {
  const tokens: OdometerToken[] = [];

  for (const char of value) {
    if (/\p{Nd}/u.test(char)) {
      tokens.push({ type: "digit", value: Number(char) });
    } else {
      tokens.push({ type: "literal", value: char });
    }
  }

  return tokens;
}

/** Percentage offset for a digit strip: 0 at progress 0, digit×10 at progress 1. */
export function odometerDigitTranslatePercent(
  digit: number,
  progress: number
): number {
  return digit * progress * 10;
}

export interface AnimatedPricingCardProps {
  copy?: TermsCopy;
  className?: string;
  motionDebug?: TermsMotionDebugProps;
}

function usePricingPlaybackPhase(
  state: TermsMotionState,
  phase: number | undefined,
  reducedMotion: boolean,
  durationMs: number
): number {
  const [playbackPhase, setPlaybackPhase] = useState(() => {
    if (phase !== undefined) return phase;
    return 1;
  });
  const playbackStartRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (phase !== undefined) {
      setPlaybackPhase(phase);
      return;
    }

    if (reducedMotion || state === "complete") {
      setPlaybackPhase(1);
      return;
    }

    if (state === "idle") {
      setPlaybackPhase(0);
      return;
    }

    if (state !== "playing") return;

    playbackStartRef.current = performance.now();

    const tick = (timestamp: number) => {
      const start = playbackStartRef.current ?? timestamp;
      const linear = Math.min((timestamp - start) / durationMs, 1);
      setPlaybackPhase(linear);
      if (linear < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [state, phase, reducedMotion, durationMs]);

  return phase !== undefined ? phase : playbackPhase;
}

function OdometerDigitColumn({
  digit,
  progress,
}: {
  digit: number;
  progress: number;
}) {
  const stripStyle: CSSProperties = {
    transform: `translateY(-${odometerDigitTranslatePercent(digit, progress)}%)`,
  };

  return (
    <span className="inline-block h-[1.2em] overflow-hidden align-baseline leading-[1.2]">
      <span
        className="flex flex-col"
        style={stripStyle}
        data-testid="odometer-digit-strip"
        aria-hidden
      >
        {Array.from({ length: 10 }, (_, index) => (
          <span key={index} className="h-[1.2em] leading-[1.2] tabular-nums">
            {index}
          </span>
        ))}
      </span>
    </span>
  );
}

function OdometerValue({
  value,
  progress,
}: {
  value: string;
  progress: number;
}) {
  const tokens = tokenizeOdometerValue(value);

  return (
    <span>
      <span className="sr-only">{value}</span>
      <span className="inline-flex items-baseline" aria-hidden>
        {tokens.map((token, index) =>
          token.type === "literal" ? (
            <span key={index} className="whitespace-pre">
              {token.value}
            </span>
          ) : (
            <OdometerDigitColumn
              key={index}
              digit={token.value}
              progress={progress}
            />
          )
        )}
      </span>
    </span>
  );
}

export function AnimatedPricingCard({
  copy = enLiveHomeCopy.terms,
  className,
  motionDebug,
}: AnimatedPricingCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { state, phase, reducedMotion } = useTermsCardMotion(
    ref,
    PRICING_DURATION_MS,
    motionDebug
  );
  const effectivePhase = usePricingPlaybackPhase(
    state,
    phase,
    reducedMotion,
    PRICING_DURATION_MS
  );

  const financingProgress = segmentProgress(
    effectivePhase,
    FINANCING_START,
    FINANCING_END
  );
  const feeProgress = segmentProgress(effectivePhase, FEE_START, FEE_END);
  const totalProgress = segmentProgress(effectivePhase, TOTAL_START, TOTAL_END);
  const isDebugScrub = phase !== undefined;
  const entranceProgress = isDebugScrub
    ? undefined
    : pricingPlaybackEase(
        segmentProgress(effectivePhase, 0, MOCK_ENTRANCE_END)
      );

  return (
    <div
      ref={ref}
      data-testid="terms-pricing-animated"
      className={cn(
        "border-grey-100 bg-grey-0 relative overflow-hidden rounded-2xl border",
        TERMS_TOP_ROW_CARD_HEIGHT,
        className
      )}
    >
      <div className="flex flex-col gap-3 px-[30px] pt-[31px] lg:px-10 lg:pt-12">
        <h3 className="text-grey-950 text-[20px] leading-[1.3] tracking-[-0.04em] lg:text-[24px] lg:leading-[1.2]">
          {copy.pricingTitle}
        </h3>
        <p className="text-grey-600 max-w-[268px] text-[14px] leading-[1.5] tracking-[-0.28px] lg:max-w-[391px] lg:text-[16px] lg:tracking-[-0.02em]">
          {copy.pricingDescription}
        </p>
      </div>

      <PricingBrowserMock
        copy={copy}
        motionState={state}
        phase={effectivePhase}
        entranceProgress={entranceProgress}
        financingAmount={
          <OdometerValue
            value={copy.mockFinancingAmount}
            progress={financingProgress}
          />
        }
        feeAmount={
          <OdometerValue value={copy.mockFeeAmount} progress={feeProgress} />
        }
        totalAmount={
          <OdometerValue
            value={copy.mockTotalAmount}
            progress={totalProgress}
          />
        }
      />
    </div>
  );
}
